import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchEntries, fetchSections, deleteEntry } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { ChevronLeft, Trash2 } from "lucide-react";
import SpeakButton from "@/components/SpeakButton";
import NarrateInMyVoice from "@/components/NarrateInMyVoice";
import { getReflectionVoice } from "@/lib/reflectionVoice";
import { MOODS } from "@/components/MoodPicker";
import UserBadge from "@/components/UserBadge";
import ReflectionThread from "@/components/ReflectionThread";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function History() {
  const [entries, setEntries] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const reflectionVoice = getReflectionVoice(user?.preferred_voice);

  useEffect(() => {
    (async () => {
      try {
        const [ents, secs] = await Promise.all([fetchEntries(), fetchSections()]);
        setEntries(ents);
        setSections(secs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sectionTitle = (id) => sections.find((s) => s.id === id)?.title || id;

  const handleDelete = async (id) => {
    try {
      await deleteEntry(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry released.");
    } catch (e) {
      console.error(e);
      toast.error("Could not delete this entry.");
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-16 pt-10 sm:pt-14 md:pt-20 pb-32 relative z-10">
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 min-h-[44px] -mx-2 px-2 sm:min-h-0 sm:mx-0 sm:px-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
            data-testid={TID.backToDashboard}
          >
            <ChevronLeft className="h-3 w-3" /> The Workbook
          </Link>
          <UserBadge />
        </div>

        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">Archive</p>
        <h1
          data-testid={TID.historyTitle}
          className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-10 sm:mb-14"
        >
          Everything you have <em className="text-bone-300 not-italic italic">spoken.</em>
        </h1>

        {(() => {
          if (loading) {
            return <p className="font-sans text-bone-400">Gathering your words…</p>;
          }
          if (entries.length === 0) {
            return (
              <div className="border border-obsidian-600 p-12 text-center">
                <p className="font-serif text-xl text-bone-300 mb-4">The archive is quiet.</p>
                <p className="font-sans text-sm text-bone-500 mb-8">Begin a chapter, and your reflections will land here.</p>
                <Link
                  to="/dashboard"
                  className="inline-block font-sans text-xs uppercase tracking-[0.24em] text-terracotta hover:text-terracotta-soft border-b border-terracotta transition-colors pb-1"
                >
                  Return to the workbook
                </Link>
              </div>
            );
          }
          return (
          <div className="space-y-8">
            {entries.map((e) => (
              <article
                key={e.id}
                data-testid={TID.entryItem(e.id)}
                className="border-b border-obsidian-600 pb-8 last:border-b-0 group"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
                      {sectionTitle(e.section_id)} · {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                    <p className="font-serif text-lg md:text-xl text-bone-100 leading-snug italic">
                      {e.prompt_text}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
                    <SpeakButton
                      text={e.prompt_text}
                      size="sm"
                      testId={`speak-history-prompt-${e.id}`}
                      label="Read prompt aloud"
                    />
                    <SpeakButton
                      text={e.answer}
                      size="sm"
                      testId={`speak-entry-${e.id}`}
                      label="Read entry aloud"
                    />
                    <NarrateInMyVoice entryId={e.id} size="sm" />
                    <button
                      data-testid={TID.deleteEntryBtn(e.id)}
                      onClick={() => handleDelete(e.id)}
                      className="text-bone-400 hover:text-terracotta p-3 -mr-2 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      aria-label="Delete entry"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="font-sans text-base text-bone-300 leading-relaxed whitespace-pre-wrap">{e.answer}</p>
                {e.mood && (() => {
                  const m = MOODS.find((x) => x.id === e.mood);
                  if (!m) return null;
                  return (
                    <div className="mt-4 inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-400">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: m.color }} />
                      Body noted · {m.label}
                    </div>
                  );
                })()}
                {e.reflection && (
                  <div className="mt-6" data-testid={`history-mirror-${e.id}`}>
                    <ReflectionThread
                      promptId={e.prompt_id || e.id}
                      entry={e}
                      reflection={e.reflection}
                      reflectionVoice={reflectionVoice}
                      autoPlayFirst={false}
                      onTurnsUpdate={(newTurns) => {
                        // Keep History state in sync so re-renders and other
                        // entries seeing the same day-cap update correctly.
                        setEntries((prev) =>
                          prev.map((ent) =>
                            ent.id === e.id ? { ...ent, turns: newTurns } : ent
                          )
                        );
                      }}
                    />
                  </div>
                )}
              </article>
            ))}
          </div>
          );
        })()}
      </div>
    </div>
  );
}
